import { Stack } from "@mui/material";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_APP_EVENTS, APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogErrorEffect } from "@src/appV2/lib/analytics/useLogErrorEffect";
import { Illustration, Text, Title } from "@src/appV2/redesign/system/ui-components";

import { Button } from "../../components/Button";
import { PageFooterWrapper } from "../../components/PageFooterWrapper";
import { PageWrapper } from "../../components/PageWrapper";
import { PageContent } from "../../WorkerShifts/components/PageContent";
import { OnboardingPageHeader, type OnboardingPageHeaderProps } from "../SharedPageHeader";
import { type OnboardingPersonalIdVerificationAnalytics } from "./types";

interface OnboardingPersonalIdNotRequiredProps extends Pick<OnboardingPageHeaderProps, "progress"> {
  onNext: () => Promise<void>;
  onBack: () => void;
  analytics: OnboardingPersonalIdVerificationAnalytics;
}

export function OnboardingPersonalIdNotRequired({
  onNext,
  onBack,
  analytics,
  progress,
}: OnboardingPersonalIdNotRequiredProps) {
  const isSupportPairingEnabled = useCbhFlag(CbhFeatureFlag.ENABLE_SUPPORT_PAIRING, {
    defaultValue: false,
  });

  useLogErrorEffect(
    APP_V2_APP_EVENTS.ONBOARDING_PERSONAL_ID_NOT_REQUIRED_ERROR,
    {
      error: new Error("Personal ID verification was not required during onboarding"),
      metadata: {
        ...analytics,
      },
    },
    {
      enabled: !isSupportPairingEnabled,
    }
  );

  return (
    <PageWrapper variant="tertiary">
      <OnboardingPageHeader progress={progress} onClickBack={onBack} />
      <PageContent>
        <Stack
          gap={4}
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          flex={1}
          padding={4}
        >
          <Illustration type="error" sx={{ alignSelf: "center" }} />
          <Title variant="h3" component="h1">
            ID verification unavailable
          </Title>
          <Text variant="body1">
            We&apos;re having trouble loading this step. No worries, you can complete the rest of
            your signup and verify your ID later.
          </Text>
        </Stack>
      </PageContent>
      <PageFooterWrapper>
        <Button
          fullWidth
          variant="brand"
          size="large"
          onClick={() => {
            logEvent(APP_V2_USER_EVENTS.ONBOARDING_PERSONAL_ID_NOT_REQUIRED_CONTINUE_CLICKED, {
              workerId: analytics.workerId,
            });
            void onNext();
          }}
        >
          Continue
        </Button>
      </PageFooterWrapper>
    </PageWrapper>
  );
}
